import { removeTokenCookie } from '@root/utils/cookies/cookies';
import getConfig from 'next/config';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'stylus-ui/Toast';
import { safeJSONParse } from 'utils/utils';

import { useAccountInfo } from '../../../hooks/account/useAccountInfo';
import authService from '../../../services/authServices';
import { authSlice, loginWithGoogle, resetAuthData, setErrorMessage, setToken } from '../../../slices/authSlice';
import { useAuthenticationFormProps } from '../../../utils/authentication';
import { parseTokenToJSON } from '../../../utils/tokenUtil';
import { trackAccountCreatedForGoogleSSO, trackEvent, trackSignIn, trackSignup } from '../../../utils/tracking';
import { removeCurrentUrlQueryParamWithoutReRendering } from '../../../utils/urls';
import { DispatchType, GoogleLoginTokenInfo } from '../../types';
import AuthPage from '../atoms/AuthPage';
import LoginForm from '../forms/LoginForm/LoginForm';

const { publicRuntimeConfig } = getConfig();
const GOOGLE_REDIRECT_URL = publicRuntimeConfig.googleOauthRedirectUrl;

const SigninPage = ({ isHandlingAuthentication }: { isHandlingAuthentication?: boolean }) => {
  const dispatch = useDispatch<DispatchType>();
  const router = useRouter();
  const [isSigningInWithSsoToken, setIsSigningInWithSsoToken] = useState(false); // flag to indicate if we are signing in with a sso token

  const {
    t: token,
    err,
    is_signup: isSignup,
    auth_method: authMethod,
    redirect_to_desktop: redirectToDesktop,
    redirectToComment: redirectToViewerUrl
  } = router.query;
  const accountQuery = useAccountInfo({ enabled: false });
  const autoSubmit = router.query?.autoSubmit as string;
  const backToEmbed = router.query?.backToEmbed === 'true';

  const { onSubmit, onEmailChange, onPasswordChange, ...authenticationFormProps } = useAuthenticationFormProps({
    backToEmbed,
    disableCloudflare: true,
    callback: () => {
      if (redirectToViewerUrl) {
        router.push(`${redirectToViewerUrl}${window.location.hash}`);
      }
    }
  });

  useEffect(() => {
    if (window.localStorage.getItem('userDeleted') === 'true') {
      removeTokenCookie();
      toast({
        variant: 'success',
        title: 'Your account has been deleted'
      });
      window.localStorage.removeItem('userDeleted');
    }
  }, []);

  // Used to help us switch between accounts easily in dev environments.
  useEffect(() => {
    dispatch(resetAuthData());
    if (autoSubmit) {
      const [email, password] = autoSubmit.split(':');

      onEmailChange(email);
      if (password) {
        onPasswordChange(password);
      }
      onSubmit();
    }
    if (err) {
      if (err === 'access_denied') {
        trackEvent('denied_google_permissions');
      }
      // @ts-expect-error - Should be solved when migrated to React-Query
      dispatch(setErrorMessage(err === 'access_denied' ? 'Google access denied' : err));
      removeCurrentUrlQueryParamWithoutReRendering(router, 'err');
    }
  }, []);

  const responseGoogle = async (response) => {
    if (response.code) {
      const authCode = response.code;
      const redirectURL = GOOGLE_REDIRECT_URL;
      // @ts-expect-error - Should be solved when migrated to React-Query
      const userInfo: GoogleLoginTokenInfo = await dispatch(loginWithGoogle({ authCode, redirectURL }));

      if (userInfo?.payload?.detail) {
        // @ts-expect-error - Should be solved when migrated to React-Query
        setErrorMessage(userInfo.payload.detail);
        return;
      }
      if (userInfo?.payload?.token) {
        userInfo.token = userInfo.payload.token;

        if (userInfo.payload.is_signup) {
          trackSignup('google_sso', userInfo.payload?.id);
        } else {
          trackSignIn('google_sso', userInfo.payload?.id);
        }

        onSubmit();
      }
    }
  };

  useEffect(() => {
    if (window.location.hash && window.location.hash.startsWith('#code')) {
      const originalHash = window.location.hash;

      window.location.hash = '';
      const code = originalHash.split('&')[0].replace('#code=', '');

      responseGoogle({ code });
    } else if (token) {
      const decoded = parseTokenToJSON(token);
      const authorizationMethod = authMethod || 'other';

      trackEvent('attempt_signin_with_passed_token', {
        path: router.pathname,
        user_id: decoded?.user_id,
        auth_method: authorizationMethod
      });
      setIsSigningInWithSsoToken(true);
      authService
        .refreshToken(token)
        .then(async ({ token, refresh }) => {
          // @ts-expect-error - TODO: Update types
          dispatch(setToken({ token, refresh }));
          await accountQuery.refetch();
          const decoded = parseTokenToJSON(token);

          trackEvent('successful_signin_with_passed_token', {
            path: router.pathname,
            user_id: decoded?.user_id,
            auth_method: authorizationMethod
          });
          if (isSignup !== undefined && isSignup?.toString()?.toLowerCase() === 'true') {
            trackSignup(authorizationMethod, decoded?.user_id);
            if (authorizationMethod === 'google_sso') {
              trackAccountCreatedForGoogleSSO(safeJSONParse(window.localStorage.getItem('onboardingAnswers')));
            }
          } else {
            trackSignIn(authorizationMethod, decoded?.user_id);
          }
        })
        .catch((err) => {
          if (err?.status === 403) {
            const detail = err?.detail ?? 'Your IP address is not allowed.';

            // @ts-expect-error - Should be solved when migrated to React-Query
            dispatch(setErrorMessage(`Unable to sign in. ${detail}`));
            window.history.replaceState({ ...window.history.state, as: router.pathname, url: router.pathname }, '', router.pathname);
          }
        });
      window.history.pushState('', '', window.location.href);
    }

    return () => {
      if (window.opener === null) {
        dispatch(authSlice.actions.setAuthenticatedRedirectUrl({ url: null }));
      }
    };
  }, []);

  useEffect(() => {
    // Remove the token cookie on sign in page load to ensure user can make it
    // back to the home page. If they're here, it's without an auth state.
    removeTokenCookie();
  }, []);

  return (
    <>
      <Head>
        <title>Scribe | Sign in</title>
        <meta property='og:type' content='website' />
        <meta name='description' content='Sign in to Scribe.' />
      </Head>
      <AuthPage noBlurredBackground navItems={[{ label: 'Sign Up', url: '/signup' }]}>
        <AuthPage.Modal>
          <LoginForm
            onEmailChange={onEmailChange}
            onPasswordChange={onPasswordChange}
            onSubmit={onSubmit}
            redirectToDesktop={redirectToDesktop === 'true'}
            backToEmbed={backToEmbed}
            isSigningInWithSso={isSigningInWithSsoToken && isHandlingAuthentication}
            {...authenticationFormProps}
          />
        </AuthPage.Modal>
      </AuthPage>
    </>
  );
};

export default SigninPage;
